import React from 'react';
import { Badge, Button, Card, Form } from 'react-bootstrap';
import {
  FileEarmarkImageFill,
  FileEarmarkSpreadsheetFill,
  TrashFill,
} from 'react-bootstrap-icons';
import S3MediaImage from '../Generic/S3MediaImage';
import S3MediaLink from '../Generic/S3MediaLink';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import {
  orderFileMediaKind,
  orderFileRowIsPendingUpload,
} from '../../utils/orderFiles';
import OrderFilePreviewIcon from './OrderFilePreviewIcon';

const PO_FILE_ACCEPT =
  '.pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Renders one saved or pending file row inside PO / receipt panels.
 */
function OrderFilePreviewRow({
  file,
  onDelete,
  catalogueNumber,
  category,
  onReadReceipt,
}) {
  const isSaved = file.image_url && !file.file;
  const localUrl =
    !isSaved && file.file ? URL.createObjectURL(file.file) : null;
  const mediaKind = orderFileMediaKind(file, { category });
  const showAsImage = mediaKind === 'image';

  if (orderFileRowIsPendingUpload(file)) {
    return (
      <div className="d-flex align-items-center gap-2 mb-2 text-muted">
        <span className="spinner-border spinner-border-sm" role="status" />
        <span>Uploading…</span>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-start mb-2">
      {isSaved ? (
        showAsImage ? (
          <S3MediaLink
            url={file.image_url}
            orderImageId={file.id}
            title="Open file"
            className="me-3"
          >
            <S3MediaImage
              url={file.image_url}
              orderImageId={file.id}
              alt={`order-file-${file.id}`}
              width={120}
            />
          </S3MediaLink>
        ) : (
          <S3MediaLink
            url={file.image_url}
            orderImageId={file.id}
            title="Open file"
            className="me-3"
          >
            <OrderFilePreviewIcon row={file} category={category} />
          </S3MediaLink>
        )
      ) : (
        <a
          href={localUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="me-3"
        >
          {showAsImage ? (
            <img
              src={localUrl}
              alt={`pending-${catalogueNumber}`}
              width={120}
            />
          ) : (
            <OrderFilePreviewIcon row={file} category={category} />
          )}
        </a>
      )}
      <div className="d-flex flex-column align-items-start gap-2">
        {category === 'receipt' && isSaved && onReadReceipt ? (
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={() => onReadReceipt(file)}
          >
            Read again
          </Button>
        ) : null}
        <TrashFill
          size={20}
          color="red"
          style={{ cursor: 'pointer' }}
          onClick={() => onDelete(file.id)}
        />
      </div>
    </div>
  );
}

/**
 * PO vs receipt upload panels with distinct layout and copy.
 *
 * @param {Object} props
 * @param {Array} props.poFiles
 * @param {Array} props.receiptFiles
 * @param {Function} props.onPoFilesChange
 * @param {Function} props.onReceiptFilesChange
 * @param {Function} props.onDeletePo
 * @param {Function} props.onDeleteReceipt
 * @param {string} [props.documentsError]
 * @param {boolean} [props.showDocumentsRequirement]
 * @param {string} [props.catalogueNumber]
 * @param {boolean} [props.showNotifySupplierCheckbox]
 * @param {boolean} [props.hasPendingPo]
 * @param {boolean} [props.notifySupplier]
 * @param {Function} [props.onNotifySupplierChange]
 * @param {Function|null} [props.onReadReceipt] - Saved receipt rows only. Not shown for a new file.
 */
const OrderFilesUploadPanels = ({
  poFiles,
  receiptFiles,
  onPoFilesChange,
  onReceiptFilesChange,
  onDeletePo,
  onDeleteReceipt,
  documentsError = '',
  showDocumentsRequirement = true,
  catalogueNumber = '',
  showNotifySupplierCheckbox = false,
  hasPendingPo = false,
  notifySupplier = false,
  onNotifySupplierChange,
  onReadReceipt = null,
}) => {
  return (
    <div className="field-margin">
      <Form.Label className="fw-semibold">
        Order documents
        {showDocumentsRequirement && (
          <>
            {' '}
            <RequiredAsteriskComponent />
          </>
        )}
      </Form.Label>
      <p className="text-muted small mb-3">
        Upload at least one file: a purchase order (PO) <strong>or</strong> a
        receipt/invoice (both optional together, one required).
      </p>
      {documentsError ? (
        <div className="alert alert-danger py-2 small" role="alert">
          {documentsError}
        </div>
      ) : null}

      <Card className="mb-3 order-doc-panel">
        <Card.Body>
          <Card.Title className="h6 d-flex align-items-center gap-2 mb-1">
            <FileEarmarkSpreadsheetFill />
            Purchase order (PO)
            <Badge bg="secondary">PO</Badge>
          </Card.Title>
          <Card.Text className="small text-muted mb-2">
            Supplier PO (PDF, CSV, or Excel) — stored in the system only,{' '}
            <strong>not</strong> attached to the daily finance email.
          </Card.Text>
          <Form.Control
            type="file"
            multiple
            accept={PO_FILE_ACCEPT}
            title="Accepted formats: PDF, CSV, Excel (.xlsx, .xls)"
            onChange={e => {
              const files = Array.from(e.target.files || []);
              onPoFilesChange(files);
              e.target.value = '';
            }}
          />
          <div className="mt-2">
            {poFiles.map(file => (
              <OrderFilePreviewRow
                key={file.id}
                file={file}
                onDelete={onDeletePo}
                catalogueNumber={catalogueNumber}
                category="po"
              />
            ))}
          </div>
          {showNotifySupplierCheckbox ? (
            <div
              className="order-notify-supplier-panel"
              role="group"
              aria-labelledby="order-notify-supplier-po-label"
              onClick={e => e.stopPropagation()}
            >
              <div className="form-check mb-0">
                <input
                  type="checkbox"
                  className="form-check-input flex-shrink-0"
                  id="order-notify-supplier-po"
                  name="notifySupplier"
                  checked={Boolean(notifySupplier)}
                  onChange={e => {
                    e.stopPropagation();
                    onNotifySupplierChange?.(e.target.checked);
                  }}
                />
                <label
                  htmlFor="order-notify-supplier-po"
                  id="order-notify-supplier-po-label"
                  className="form-check-label fw-semibold text-dark lh-sm"
                >
                  Notify supplier after PO upload
                </label>
              </div>
              <p className="small text-muted mb-0 mt-2 text-start">
                {!hasPendingPo
                  ? 'Attach a PO file above for this option to apply on submit.'
                  : 'Email is sent only after the PO upload completes successfully.'}
              </p>
            </div>
          ) : null}
        </Card.Body>
      </Card>

      <Card className="mb-2 order-doc-panel order-doc-panel--receipt">
        <Card.Body>
          <Card.Title className="h6 d-flex align-items-center gap-2 mb-1">
            <FileEarmarkImageFill />
            Receipt / invoice
            <Badge bg="primary">Receipt</Badge>
          </Card.Title>
          <Card.Text className="small text-muted mb-2">
            Delivery note, invoice, or photo —{' '}
            <strong>included</strong> in the daily finance reception email.
          </Card.Text>
          <Form.Control
            type="file"
            multiple
            accept="application/pdf, image/*"
            title="Accepted formats: pdf, jpg, png, gif"
            onChange={e => {
              const files = Array.from(e.target.files || []);
              onReceiptFilesChange(files);
              e.target.value = '';
            }}
          />
          <div className="mt-2">
            {receiptFiles.map(file => (
              <OrderFilePreviewRow
                key={file.id}
                file={file}
                onDelete={onDeleteReceipt}
                catalogueNumber={catalogueNumber}
                category="receipt"
                onReadReceipt={onReadReceipt}
              />
            ))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderFilesUploadPanels;
